import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../services/api';
import { useAuthStore, type User } from '../store/authStore';
import { useUIStore } from '../store/uiStore';

const profileSchema = z.object({
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  phone: z.string().max(50).optional().or(z.literal('')),
  language: z.enum(['es', 'en']),
});

type ProfileForm = z.infer<typeof profileSchema>;

interface ProfileApiResponse {
  data: User & { created_at: string };
}

export default function ProfilePage(): React.ReactElement {
  const { t } = useTranslation('common');
  const { setUser } = useAuthStore();
  const { setLocale } = useUIStore();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: 'success' | 'error' }>({
    visible: false,
    message: '',
    type: 'success',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<ProfileApiResponse>('/v1/users/me'),
  });

  const profile = profileData?.data;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || '',
        language: profile.language as 'es' | 'en',
      });
    }
  }, [profile, reset]);

  const showToast = (message: string, type: 'success' | 'error'): void => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((s) => ({ ...s, visible: false })), 3000);
  };

  const avatarMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      return api.patch<ProfileApiResponse>('/v1/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      const updated = response.data;
      setUser(updated);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      showToast(t('profile.avatarUpdated', 'Foto actualizada'), 'success');
    },
    onError: () => {
      showToast(t('errors.generic'), 'error');
    },
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      showToast(t('profile.invalidFileType', 'Solo se permiten JPG, PNG o WebP'), 'error');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast(t('profile.fileTooLarge', 'La imagen no debe superar 2MB'), 'error');
      return;
    }

    avatarMutation.mutate(file);
    // Reset input so re-selecting the same file triggers onChange
    e.target.value = '';
  };

  const getInitials = (): string => {
    const first = profile?.first_name?.[0] ?? '';
    const last = profile?.last_name?.[0] ?? '';
    return (first + last).toUpperCase() || '?';
  };

  const mutation = useMutation({
    mutationFn: (data: ProfileForm) => api.patch<ProfileApiResponse>('/v1/users/me', data),
    onSuccess: (response) => {
      const updated = response.data;
      setUser(updated);
      setLocale(updated.language as 'es' | 'en');
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      showToast(t('buttons.save') + ' OK', 'success');
    },
    onError: () => {
      showToast(t('errors.generic'), 'error');
    },
  });

  const onSubmit = (data: ProfileForm): void => {
    mutation.mutate(data);
  };

  if (isLoading) {
    return <span className="loading loading-spinner loading-lg" />;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">{t('nav.profile')}</h1>

      <div className="card bg-base-100 shadow-xl max-w-xl">
        <div className="card-body">
          {/* Avatar section */}
          <div className="flex flex-col items-center mb-6">
            <div
              className="relative cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                  className="w-24 h-24 rounded-full object-cover ring ring-primary ring-offset-base-100 ring-offset-2"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary text-primary-content flex items-center justify-center text-3xl font-bold ring ring-primary ring-offset-base-100 ring-offset-2">
                  {getInitials()}
                </div>
              )}
              {avatarMutation.isPending ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                  <span className="loading loading-spinner loading-md text-white" />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm mt-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarMutation.isPending}
            >
              {t('profile.changePhoto', 'Cambiar foto')}
            </button>
          </div>

          {/* Read-only info */}
          <div className="mb-4">
            <p className="text-sm text-base-content/60">Email: {profile?.email}</p>
            <p className="text-sm text-base-content/60">Rol: {profile?.role}</p>
          </div>

          {/* Editable form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('nav.profile')}</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.first_name ? 'input-error' : ''}`}
                {...register('first_name')}
              />
              {errors.first_name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.first_name.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">Apellido</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.last_name ? 'input-error' : ''}`}
                {...register('last_name')}
              />
              {errors.last_name && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.last_name.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">Telefono</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${errors.phone ? 'input-error' : ''}`}
                {...register('phone')}
              />
              {errors.phone && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.phone.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('common.language')}</span>
              </label>
              <Controller
                name="language"
                control={control}
                render={({ field }) => (
                  <select className="select select-bordered w-full" {...field}>
                    <option value="es">{t('common.spanish')}</option>
                    <option value="en">{t('common.english')}</option>
                  </select>
                )}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary mt-2"
              disabled={!isDirty || mutation.isPending}
            >
              {mutation.isPending ? <span className="loading loading-spinner loading-sm" /> : t('buttons.save')}
            </button>
          </form>
        </div>
      </div>

      {/* Toast notification */}
      {toast.visible && (
        <div className="toast toast-end toast-bottom">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
